import React, { useState, useEffect } from "react";
import axios from "axios";
import UserPatternSettingUpdate from "./_111_user_pattern_setting_update.js";
import { logEvent } from "./eventLogger.js";
import { getCachedPatternData, setCachedPatternData } from "./patterns/cacheHelper.js";
import { Link } from "react-router-dom";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
    Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";
import { useDateRange } from "./dateRangeContext.js";
import { formatDateMMDDYY } from "./patterns/helpers.js";
import "./styles.css";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
    Filler
);

const MODULE_NAME = "_93_pattern_scalping_reco";
const screen = "pg_patterns";

// --- Gear Icon Unicode ---
const GearIcon = ({ size = 24, color = "#fff" }) => (
    <span
        style={{
            fontSize: size,
            color,
            verticalAlign: "middle",
            cursor: "pointer",
            userSelect: "none",
            lineHeight: 1,
            display: "inline-block",
        }}
        title="Settings"
        aria-label="Settings"
    >
        &#9881;
    </span>
);

const Pattern_scalping_reco = ({ userId: propUserId }) => {
    const { windowDays } = useDateRange();

    // Derived userId with fallback
    const storedId = sessionStorage.getItem("user_id");
    const userId = propUserId || (storedId && !isNaN(Number(storedId)) ? Number(storedId) : 1);

    const [date, setDate] = useState("");
    const [tooltip, setTooltip] = useState("");
    const [scalpingPattern, setScalpingPattern] = useState(null);
    const [totalPL, setTotalPL] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [chartMode, setChartMode] = useState("cumulative");
    const [showSettings, setShowSettings] = useState(false);
    const [patternId, setPatternId] = useState(null);
    const [patternConfig, setPatternConfig] = useState(null);
    const [fromCache, setFromCache] = useState(false);
    const [showTable, setShowTable] = useState(false);

    // Helper to parse windowDays to a valid lookback number
    function getLookbackDays(val) {
        if (!val) return 90;
        if (!isNaN(Number(val))) return Number(val);
        if (val === "YTD") return 365;
        return 90;
    }

    // ✅ UPDATED: Fetch data with async database cache and userKey for symbol decryption
    const fetchPatternData = async (lookback) => {
        setLoading(true);
        
        try {
            // 🔓 NEW: Retrieve userKey from sessionStorage for decryption
            const userKey = sessionStorage.getItem("userKey");
            const fetchParams = { 
                lookback_days: lookback 
            };

            // Inject userKey if Privacy Mode is active
            if (userKey) {
                fetchParams.userKey = userKey;
            }

            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/_93_pattern_scalping_reco`,
                {
                    params: fetchParams,
                    withCredentials: true,
                }
            );
            
            setDate(res.data.date);
            setTooltip(res.data.tooltip);
            setScalpingPattern(res.data);
            setTotalPL(res.data.total_pl || 0);

            // ✅ UPDATED: Extract numeric pattern_id from API response
            const numericPatternId = res.data.pattern_id;
            setPatternId(numericPatternId);
            setPatternConfig(res.data.config || {});

            // ✅ UPDATED: Use numeric pattern_id as cache key
            if (numericPatternId) {
                await setCachedPatternData(
                    `pattern_${numericPatternId}`,
                    lookback,
                    res.data
                );
            }

            console.log("Pattern ID:", numericPatternId, "Config:", res.data.config);
            setFromCache(false);
        } catch (err) {
            console.error("Error fetching pattern data:", err);
            
            // ✅ Try to load from cache on error
            if (patternId) {
                const cachedData = await getCachedPatternData(
                    `pattern_${patternId}`,
                    lookback
                );
                if (cachedData) {
                    setDate(cachedData.date);
                    setTooltip(cachedData.tooltip);
                    setScalpingPattern(cachedData);
                    setTotalPL(cachedData.total_pl || 0);
                    setPatternConfig(cachedData.config || {});
                    setFromCache(true);
                    setLoading(false);
                    return;
                }
            }
            
            setTooltip("Error fetching data.");
            setScalpingPattern(null);
            setTotalPL(null);
            setPatternId(null);
        }
        
        setLoading(false);
    };

    useEffect(() => {
        const lookback = getLookbackDays(windowDays);
        if (lookback) {
            fetchPatternData(lookback);
        }
        // eslint-disable-next-line
    }, [windowDays]);

    // Tooltip icon
    const tooltipIcon = tooltip ? (
        <span
            className="tooltip-icon"
            style={{
                marginLeft: 6,
                verticalAlign: "middle",
                cursor: "pointer",
                fontSize: "1.1em",
            }}
            title={tooltip}
        >
            🛈
        </span>
    ) : null;

    // Chart data preparation
    const getChartData = () => {
        if (!scalpingPattern || !Array.isArray(scalpingPattern.time_evolution))
            return [];
        if (chartMode === "cumulative") {
            return scalpingPattern.time_evolution;
        }
        if (chartMode === "rolling") {
            const arr = scalpingPattern.time_evolution;
            const window = 5;
            return arr.map((d, i) => {
                const slice = arr.slice(Math.max(0, i - window + 1), i + 1);
                const avgPL =
                    slice.reduce((sum, x) => sum + (x.daily_pl || 0), 0) / slice.length;
                const avgWin =
                    (slice.reduce((sum, x) => sum + (x.daily_wins || 0), 0) /
                        (slice.reduce((sum, x) => sum + (x.daily_trades || 0), 0) || 1)) *
                    100;
                const avgFreq =
                    slice.reduce((sum, x) => sum + (x.frequency || 0), 0) / slice.length;
                return {
                    ...d,
                    rolling_pl: avgPL,
                    rolling_win_rate: avgWin,
                    rolling_frequency: avgFreq,
                };
            });
        }
        return [];
    };

    // Helper to get chart labels in MM/DD/YY format
    const getChartLabels = (data) =>
        data.map((d) => formatDateMMDDYY(d.date));

    // Prepare Chart.js datasets
    const buildChartData = () => {
        const data = getChartData();
        if (!data.length) return { labels: [], datasets: [] };

        const plKey = chartMode === "rolling" ? "rolling_pl" : "cumulative_pl";
        const winKey = chartMode === "rolling" ? "rolling_win_rate" : "cumulative_win_rate";
        const freqKey = chartMode === "rolling" ? "rolling_frequency" : "frequency";
        const winsKey = chartMode === "rolling" ? "rolling_wins" : "daily_wins";

        // Calculate wins and losses for each data point
        const wins = data.map(d => d[winsKey] || 0);
        const freq = data.map(d => d[freqKey] || 0);
        const losses = freq.map((f, i) => Math.max(0, f - wins[i]));

        return {
            labels: getChartLabels(data),
            datasets: [
                {
                    type: "line",
                    label: "P/L",
                    data: data.map((d) => d[plKey]),
                    borderColor: "#1976d2",
                    backgroundColor: "rgba(25, 118, 210, 0.1)",
                    borderWidth: 3,
                    pointRadius: 3,
                    fill: true,
                    tension: 0.3,
                    yAxisID: "y",
                },
                {
                    type: "line",
                    label: "Win Rate %",
                    data: data.map((d) => d[winKey]),
                    borderColor: "#4caf50",
                    backgroundColor: "rgba(76, 175, 80, 0.1)",
                    borderWidth: 2,
                    pointRadius: 2,
                    fill: true,
                    tension: 0.3,
                    yAxisID: "y1",
                },
                {
                    type: "bar",
                    label: "Wins",
                    data: wins,
                    backgroundColor: "rgba(76, 175, 80, 0.6)",
                    borderColor: "#4caf50",
                    borderWidth: 1,
                    yAxisID: "y2",
                    stack: "frequency",
                },
                {
                    type: "bar",
                    label: "Losses",
                    data: losses,
                    backgroundColor: "rgba(244, 67, 54, 0.6)",
                    borderColor: "#F44336",
                    borderWidth: 1,
                    yAxisID: "y2",
                    stack: "frequency",
                },
            ],
        };
    };

    const chartData = buildChartData();
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: true, position: "top" },
            title: { display: false },
        },
        scales: {
            y: {
                type: "linear",
                position: "left",
                title: { display: true, text: "P/L ($)" },
                ticks: { color: "#fff" },
                grid: { color: "#444" },
            },
            y1: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Win Rate (%)" },
                ticks: { color: "#fff" },
                grid: { drawOnChartArea: false },
            },
            y2: {
                type: "linear",
                position: "right",
                title: { display: true, text: "Frequency" },
                ticks: { color: "#fff" },
                grid: { drawOnChartArea: false },
                offset: true,
            },
            x: {
                ticks: { color: "#fff" },
                grid: { color: "#444" },
            },
        },
    };

    // --- Settings Modal ---
    const renderSettingsModal = () => {
        if (!showSettings || !patternId) return null;

        return (
            <>
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        background: "rgba(24,26,37,0.5)",
                        backdropFilter: "blur(6px)",
                        zIndex: 9998,
                    }}
                    onClick={() => setShowSettings(false)}
                />
                <div
                    style={{
                        position: "fixed",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 9999,
                        minWidth: 600,
                        minHeight: 420,
                        background: "#181A25",
                        borderRadius: 12,
                        boxShadow: "0 2px 8px rgba(0,255,255,0.08)",
                        padding: 0,
                    }}
                >
                    <UserPatternSettingUpdate
                        patternId={patternId}
                        countThreshold={patternConfig?.count_threshold}
                        thresholdDefinition={patternConfig?.threshold_definition}
                    />
                    <button
                        onClick={() => setShowSettings(false)}
                        style={{
                            position: "absolute",
                            top: 12,
                            right: 18,
                            background: "none",
                            border: "none",
                            color: "#fff",
                            fontSize: 22,
                            cursor: "pointer",
                        }}
                        title="Close"
                    >
                        ✕
                    </button>
                </div>
            </>
        );
    };

    // Pattern label/meaning and details
    const renderPatternMessages = () => {
        if (!scalpingPattern) {
            return <span>No data available.</span>;
        }

        let label = scalpingPattern.pattern_label;
        let description = scalpingPattern.pattern_meaning;

        const moreDetails = (
            <div style={{ margin: "8px 0" }} data-analytics={MODULE_NAME}>
                <a
                    href="#"
                    style={{
                        color: "#4fa3f7",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: "0.97em",
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        setShowDetails((prev) => !prev);
                    }}
                >
                    {showDetails ? "Hide details" : "More details"}
                </a>
                {showDetails && (
                    <div
                        style={{
                            marginTop: 6,
                            color: "#aaa",
                            fontSize: "0.97em",
                        }}
                    >
                        <div>
                            Lookback window: <b>{scalpingPattern.lookback_days} days</b>
                        </div>
                        <div>
                            Position size threshold: <b>{(scalpingPattern.position_pct_threshold * 100).toFixed(2)}%</b>
                        </div>
                        <div>
                            Max holding duration: <b>{scalpingPattern.max_holding_duration_minutes} minutes</b>
                        </div>
                        <div>
                            Pattern matches: <b>{scalpingPattern.pattern_matches}</b>
                        </div>
                        {scalpingPattern.recent_events_30d !== undefined && (
                            <div>
                                Recent events (30 days): <b>{scalpingPattern.recent_events_30d}</b>
                            </div>
                        )}
                        {/* ✅ NEW: Show cache status */}
                        {fromCache && (
                            <div style={{ marginTop: 6, fontSize: "0.9em", color: "#888" }}>
                                📦 Data loaded from database cache (4 hour TTL)
                            </div>
                        )}
                    </div>
                )}
            </div>
        );

        return (
            <div style={{ marginBottom: 8 }}>
                <span>
                    {label || "Emerging Pattern"}: {description}
                </span>
                {moreDetails}
            </div>
        );
    };

    // Chart mode toggles
    const renderChartToggles = () => (
        <div style={{ display: "flex", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
            {["cumulative", "rolling"].map((mode) => {
                const buttonLabels = {
                    cumulative: "Cumulative",
                    rolling: "Rolling Avg",
                };
                const buttonLabel = buttonLabels[mode];
                const subcomponentLabel = mode === "cumulative"
                    ? "Cumulative Chart"
                    : "Rolling Avg Chart";

                return (
                    <div key={mode} data-analytics={MODULE_NAME}>
                        <button
                            onClick={() => {
                                logEvent({
                                    event_type: "button_click",
                                    user_id: userId,
                                    module_name: MODULE_NAME,
                                    subcomponent: subcomponentLabel,
                                    screen: window.location.pathname,
                                    metadata: {
                                        button_link_value: buttonLabel
                                    }
                                });
                                setChartMode(mode);
                            }}
                            className={`btn ${chartMode === mode ? "btn-selected" : ""}`}
                            style={{
                                padding: "6px 14px",
                                borderRadius: 6,
                                border: "1px solid #888",
                                background: chartMode === mode ? "#1976d2" : "#f5f5f5",
                                color: chartMode === mode ? "#fff" : "#222",
                                fontWeight: 500,
                                cursor: "pointer",
                                transition: "background 0.2s",
                                fontSize: "0.98em",
                            }}
                        >
                            {buttonLabel}
                        </button>
                    </div>
                );
            })}
        </div>
    );

    // --- Table Styles ---
    const tableStyle = {
        width: "100%",
        borderCollapse: "collapse",
        marginTop: 16,
        background: "#222",
    };

    const thStyle = {
        padding: "8px 12px",
        fontSize: 13,
        textAlign: "left",
        borderBottom: "1px solid #555",
        backgroundColor: "#333",
        fontWeight: 600,
        color: "#fff",
    };

    const tdStyle = {
        padding: "8px 12px",
        borderBottom: "1px solid #444",
        fontSize: 13,
        color: "#ccc",
    };

    // --- Transaction Summary Table ---
    const renderTransactionSummary = () => {
        if (!scalpingPattern || !Array.isArray(scalpingPattern.summary_table) || scalpingPattern.summary_table.length === 0) {
            return (
                <div style={{ marginTop: 16 }}>
                    <button
                        onClick={() => setShowTable(!showTable)}
                        style={{
                            padding: "8px 16px",
                            borderRadius: 6,
                            border: "1px solid #888",
                            background: showTable ? "#1976d2" : "#333",
                            color: "#fff",
                            fontWeight: 500,
                            cursor: "pointer",
                            transition: "background 0.2s",
                            fontSize: "0.98em",
                        }}
                    >
                        {showTable ? "Hide Transaction Summary" : "Show Transaction Summary"} (0 trades)
                    </button>
                    {showTable && (
                        <div style={{ color: "#888", padding: 16, marginTop: 12 }}>
                            No transaction data available.
                        </div>
                    )}
                </div>
            );
        }

        const trades = scalpingPattern.summary_table;

        return (
            <div style={{ marginTop: 16 }}>
                <div style={{ marginBottom: 12 }}>
                    <button
                        onClick={() => setShowTable(!showTable)}
                        style={{
                            padding: "8px 16px",
                            borderRadius: 6,
                            border: "1px solid #888",
                            background: showTable ? "#1976d2" : "#333",
                            color: "#fff",
                            fontWeight: 500,
                            cursor: "pointer",
                            transition: "background 0.2s",
                            fontSize: "0.98em",
                        }}
                    >
                        {showTable ? "Hide Transaction Summary" : "Show Transaction Summary"} ({trades.length} trades)
                    </button>
                </div>

                {showTable && (
                    <div style={{ overflowX: "auto" }}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Trade Date</th>
                                    <th style={thStyle}>Symbol</th>
                                    <th style={thStyle}>P/L</th>
                                    <th style={thStyle}>Avg Duration (sec)</th>
                                    <th style={thStyle}>View Tags</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trades.map((trade, idx) => {
                                    const tradeDate = trade.trade_date;
                                    const isoDate = /^\d{2}\/\d{2}\/\d{2}$/.test(tradeDate)
                                        ? `20${tradeDate.split("/").reverse().join("-")}`
                                        : tradeDate;

                                    return (
                                        <tr key={idx}>
                                            <td style={tdStyle}>
                                                {trade.trade_date}
                                            </td>
                                            <td style={tdStyle}>
                                                {trade.symbol}
                                            </td>
                                            <td style={{
                                                ...tdStyle,
                                                color: trade.pl >= 0 ? "#4caf50" : "#e53935",
                                                fontWeight: 600,
                                            }}>
                                                ${trade.pl.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td style={tdStyle}>
                                                {trade.avg_duration_sec.toLocaleString(undefined, {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })}
                                            </td>
                                            <td style={tdStyle}>
                                                <Link
                                                    to={`/pg_details?date=${isoDate}&symbol=${trade.symbol}`}
                                                    style={{
                                                        color: "#0077cc",
                                                        textDecoration: "underline",
                                                        cursor: "pointer",
                                                        fontSize: "0.98em"
                                                    }}
                                                    title={`View tags for ${trade.symbol} on ${formatDateMMDDYY(trade.trade_date)}`}
                                                    onClick={() => {
                                                        sessionStorage.setItem("navigationDate", isoDate);
                                                        sessionStorage.setItem("navigationSymbol", trade.symbol);
                                                        logEvent({
                                                            event_type: "button_click",
                                                            user_id: userId,
                                                            module_name: MODULE_NAME,
                                                            subcomponent: "View Tags Link",
                                                            screen: window.location.pathname,
                                                            metadata: {
                                                                symbol: trade.symbol,
                                                                trade_date: isoDate,
                                                                button_link_value: "Tag details"
                                                            }
                                                        });
                                                    }}
                                                >
                                                    Tag details
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div
            style={{
                width: "100%",
                background: "none",
                borderRadius: 0,
                padding: 0,
                margin: 0,
                maxWidth: "100%",
                position: "relative",
            }}
        >
            <h3
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    fontSize: "1.25em",
                    margin: "0 0 8px 0",
                    color: "#fff",
                }}
            >
                {scalpingPattern?.pattern_name || "Scalping Pattern Recognition"}{" "}
                {tooltipIcon}
                <span
                    style={{ marginLeft: 8 }}
                    title="Edit pattern settings"
                    onClick={() => {
                        logEvent({
                            event_type: "button_click",
                            user_id: userId,
                            module_name: MODULE_NAME,
                            subcomponent: "Settings Gear",
                            screen: window.location.pathname,
                            metadata: { button_link_value: "Settings Gear" }
                        });
                        setShowSettings(true);
                    }}
                >
                    <GearIcon size={24} color="#fff" />
                </span>
            </h3>
            {renderSettingsModal()}
            <div style={{ minHeight: 32, fontSize: "1.05em", marginBottom: 16 }}>
                {loading ? (
                    <span>Loading...</span>
                ) : (
                    <>
                        {/* Header Row: Date */}
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: 12,
                                flexWrap: "wrap",
                                gap: 12,
                            }}
                        >
                            <span style={{ color: "#888",  }}>
                            </span>
                        </div>

                        {/* P/L Display */}
                        {typeof totalPL === "number" && (
                            <div
                                style={{
                                    marginTop: 6,
                                    color: "#e0b400",
                                    fontSize: "1.1em",
                                    marginBottom: 12,
                                }}
                            >
                                <b>P/L:</b>{" "}
                                <span
                                    style={{
                                        color: totalPL >= 0 ? "#4caf50" : "#e53935",
                                    }}
                                >
                                    ${totalPL.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </span>
                            </div>
                        )}

                        {/* Pattern Messages */}
                        <div style={{ marginBottom: 12 }}>{renderPatternMessages()}</div>

                        {/* Chart Section */}
                        <div style={{ marginTop: 16 }}>
                            {renderChartToggles()}
                            <div
                                style={{
                                    position: "relative",
                                    width: "100%",
                                    minHeight: 320,
                                    maxWidth: "100%",
                                }}
                            >
                                {chartData.datasets.length > 0 ? (
                                    <Chart
                                        type="bar"
                                        data={chartData}
                                        options={{
                                            ...chartOptions,
                                            responsive: true,
                                            maintainAspectRatio: false,
                                        }}
                                    />
                                ) : (
                                    <div
                                        style={{
                                            color: "#888",
                                            padding: "20px",
                                            textAlign: "center",
                                        }}
                                    >
                                        No chart data available for this period.
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Transaction Summary Table */}
                        {renderTransactionSummary()}
                    </>
                )}
            </div>
        </div>
    );
};

export default Pattern_scalping_reco;